// ScrollMemory — Commons-controlled scroll restoration.
//
// Replaces the previous unconditional ScrollToTop behavior with a
// two-behavior model:
//
//   1. Structural navigation (default): scroll to top. This is what
//      happens when a visitor navigates forward to a new surface,
//      when Commons sends them to a structural fallback (deep-linked
//      Detail → Back → Home), or when they refresh mid-flow.
//
//   2. Return navigation: if the incoming navigation carries
//      `state.restoreScroll === true` AND the destination is a
//      recognized restoration surface (Home / Category), read the
//      remembered Y and restore it after paint. Callers set this
//      hint only on Back-arrow navigations that are conceptually
//      "returning to where I was," never on outgoing links.
//
// The hint is decoupled from routing: this component never chooses a
// destination. It only decides whether the destination opens at top
// or at a remembered position.
//
// On every route change the *outgoing* pathname's scrollY is captured
// (if it was a restoration surface) so the next return-navigation can
// find something to restore. That capture is unconditional — we don't
// know at capture-time whether the visitor will come back, only that
// they *might*.
import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { remember, recall, scrollKeyForPath } from "../lib/scrollMemory";

export default function ScrollMemory() {
  const { pathname, state } = useLocation();

  // Disable native scroll restoration once at mount so the browser's
  // own memory never fights ours.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Capture-on-leave: attach the current surface's key and let the
  // effect cleanup fire when the visitor navigates away.
  //
  // Must be useLayoutEffect (not useEffect) so this effect's cleanup
  // runs BEFORE the restore effect below fires its scroll reset —
  // otherwise the restore effect would zero the scroll first and the
  // capture would read 0. React runs useLayoutEffect cleanups (in
  // reverse declaration order) before useLayoutEffect bodies, so
  // declaring capture BEFORE restore keeps ordering correct.
  useLayoutEffect(() => {
    const key = scrollKeyForPath(pathname);
    if (!key) return undefined;
    return () => {
      remember(key, window.scrollY || window.pageYOffset || 0);
    };
  }, [pathname]);

  // Restore-or-reset on arrival. Runs pre-paint so there's no flash
  // of the top before we jump. If `state.restoreScroll` is set and
  // the destination is a restoration surface with a remembered Y,
  // scroll there; otherwise scroll to top (structural default).
  //
  // We can't assume the destination's scroll extent is stable by the
  // first paint — a category page whose 3 cards mount over multiple
  // frames may briefly be shorter than the remembered Y (browsers
  // silently clamp scrollTo to the current extent). So we retry
  // across a bounded number of animation frames, stopping as soon as
  // scrollY matches the remembered Y (page grew enough) or after
  // ~12 frames (~200ms) — after which we take whatever the page
  // gave us and never fight legitimate user scrolling.
  useLayoutEffect(() => {
    const key = scrollKeyForPath(pathname);
    const wantRestore = !!(state && state.restoreScroll);
    const remembered = wantRestore && key ? recall(key) : undefined;

    if (typeof remembered !== "number") {
      window.scrollTo(0, 0);
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const settle = () => {
      if (cancelled) return;
      window.scrollTo(0, remembered);
      const current = window.scrollY || window.pageYOffset || 0;
      if (Math.abs(current - remembered) < 2 || attempts >= 12) return;
      attempts += 1;
      requestAnimationFrame(settle);
    };
    settle();
    return () => { cancelled = true; };
  }, [pathname, state]);

  return null;
}
